import React, { useState, useMemo, useEffect, useRef } from 'react';
import './AnalyticsDashboard.css';

// ── Reusable SVG line chart ────────────────────────────────────────────────
const LineChart = ({ data, color = '#4299e1', height = 80, label, thresholdWarn, thresholdCrit }) => {
  if (!data || data.length < 2) return <div className="chart-empty">No data</div>;
  const W = 340; const H = height; const pad = 6;
  const min = Math.min(...data) * 0.97;
  const max = Math.max(...data) * 1.03;
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (W - pad * 2);
    const y = H - pad - ((v - min) / range) * (H - pad * 2);
    return [x, y];
  });
  const ptsStr = pts.map(p => p.join(',')).join(' ');
  const areaClose = `${pts[pts.length-1][0]},${H-pad} ${pts[0][0]},${H-pad}`;
  const uid = `lc-${label}`;

  const toY = v => H - pad - ((v - min) / range) * (H - pad * 2);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="line-chart" preserveAspectRatio="none">
      <defs>
        <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
        <clipPath id={`clip-${uid}`}>
          <rect x={pad} y={pad} width={W-pad*2} height={H-pad*2} />
        </clipPath>
      </defs>
      <polygon points={`${ptsStr} ${areaClose}`} fill={`url(#${uid})`} clipPath={`url(#clip-${uid})`} />
      {thresholdWarn && (
        <line x1={pad} y1={toY(thresholdWarn)} x2={W-pad} y2={toY(thresholdWarn)}
          stroke="#f6ad55" strokeWidth="1" strokeDasharray="4,3" opacity="0.7" />
      )}
      {thresholdCrit && (
        <line x1={pad} y1={toY(thresholdCrit)} x2={W-pad} y2={toY(thresholdCrit)}
          stroke="#fc8181" strokeWidth="1" strokeDasharray="4,3" opacity="0.7" />
      )}
      <polyline points={ptsStr} fill="none" stroke={color} strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]} r="3.5" fill={color} />
      <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]} r="6" fill={color} opacity="0.2" />
    </svg>
  );
};

// ── Bar chart ──────────────────────────────────────────────────────────────
const BarChart = ({ data, height = 90 }) => {
  if (!data || data.length === 0) return <div className="chart-empty">No data</div>;
  const W = 340; const H = height; const pad = 6;
  const max = Math.max(...data.map(d => d.value), 1);
  const bw = (W - pad * 2) / data.length - 4;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="bar-chart" preserveAspectRatio="none">
      {data.map((d, i) => {
        const bh = Math.max(2, ((d.value / max) * (H - pad * 2 - 16)));
        const x = pad + i * ((W - pad * 2) / data.length) + 2;
        const y = H - pad - bh - 14;
        return (
          <g key={i}>
            <rect x={x} y={y} width={bw} height={bh} fill={d.color || '#4299e1'} rx="3" opacity="0.85" />
            <text x={x + bw/2} y={H - 3} textAnchor="middle" fill="#718096" fontSize="9">
              {d.label}
            </text>
            <text x={x + bw/2} y={y - 2} textAnchor="middle" fill={d.color || '#4299e1'} fontSize="9" fontWeight="600">
              {d.value}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ── Gauge (half-circle) ────────────────────────────────────────────────────
const Gauge = ({ value, max, color, unit = '' }) => {
  const pct  = Math.min(value / max, 1);
  const r    = 38; const cx = 52; const cy = 52;
  const toRad = d => (d * Math.PI) / 180;
  const arc = (startDeg, endDeg, radius) => {
    const s = { x: cx + radius * Math.cos(toRad(startDeg)), y: cy + radius * Math.sin(toRad(startDeg)) };
    const e = { x: cx + radius * Math.cos(toRad(endDeg)),   y: cy + radius * Math.sin(toRad(endDeg)) };
    const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${radius} ${radius} 0 ${large} 1 ${e.x} ${e.y}`;
  };
  const endAngle = 180 - pct * 180;
  return (
    <svg viewBox="0 0 104 58" className="gauge-svg">
      <path d={arc(180, 0, r)} fill="none" stroke="#2d3748" strokeWidth="9" strokeLinecap="round" />
      {pct > 0.005 && (
        <path d={arc(180, endAngle, r)} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" />
      )}
      <text x={cx} y={cy - 2} textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="700">
        {Math.round(value)}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="#718096" fontSize="8">{unit}</text>
    </svg>
  );
};

// ── OEE Ring ───────────────────────────────────────────────────────────────
const OEERing = ({ value, label, color }) => {
  const R = 28; const cx = 36; const cy = 36;
  const circ = 2 * Math.PI * R;
  const dash = (value / 100) * circ;
  return (
    <svg viewBox="0 0 72 72" className="oee-ring">
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="#2d3748" strokeWidth="7" />
      <circle cx={cx} cy={cy} r={R} fill="none" stroke={color} strokeWidth="7"
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeDashoffset={circ * 0.25}
        strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`} />
      <text x={cx} y={cx + 4} textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="700">
        {value}%
      </text>
    </svg>
  );
};

// ── Sparkline ─────────────────────────────────────────────────────────────
const Sparkline = ({ data, color }) => {
  if (!data || data.length < 2) return null;
  const W = 80; const H = 28; const pad = 2;
  const min = Math.min(...data); const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (W - pad * 2);
    const y = H - pad - ((v - min) / range) * (H - pad * 2);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: 80, height: 28 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5"
        strokeLinejoin="round" strokeLinecap="round" opacity="0.8" />
    </svg>
  );
};

// ── Constants ──────────────────────────────────────────────────────────────
const SENSOR_LABELS  = {
  sensorAReading: 'Temperature',
  sensorBReading: 'Pressure',
  sensorCReading: 'Vibration',
  sensorDReading: 'Speed',
};
const SENSOR_UNITS = {
  sensorAReading: '°C',
  sensorBReading: 'PSI',
  sensorCReading: 'Hz',
  sensorDReading: 'RPM',
};
const SENSOR_THRESHOLDS = {
  sensorAReading: { warn: 90,  crit: 100, max: 120 },
  sensorBReading: { warn: 60,  crit: 70,  max: 80  },
  sensorCReading: { warn: 120, crit: 140, max: 160 },
  sensorDReading: { warn: 75,  crit: 85,  max: 100 },
};
const SENSOR_COLORS = {
  sensorAReading: '#f6ad55',
  sensorBReading: '#4299e1',
  sensorCReading: '#9f7aea',
  sensorDReading: '#48bb78',
};
const PRODUCTION_LINES = ['Outer Ring', 'Inner Ring', 'Ball Element'];

const healthColor = (v, t) =>
  v >= t.crit ? '#fc8181' : v >= t.warn ? '#f6ad55' : '#48bb78';

// ── Generate richer historical data (trend + noise + spikes) ─────────────
const buildHistorical = (baseline, points, warn, crit) => {
  const arr = [];
  let v = baseline * 0.9;  // start slightly below baseline
  for (let i = 0; i < points; i++) {
    const phase = i / points;
    v = baseline * (0.9 + 0.12 * phase);   // gradual rise to baseline
    const noise  = (Math.random() - 0.5) * baseline * 0.05;
    const spike  = (i % Math.floor(points / 4) === 0 && Math.random() > 0.55)
      ? baseline * (0.06 + Math.random() * 0.06) : 0;
    v = Math.max(baseline * 0.6, Math.min(crit * 1.05, v + noise + spike));
    arr.push(+v.toFixed(1));
  }
  arr[arr.length - 1] = baseline; // land on current value
  return arr;
};

// ── Main Component ─────────────────────────────────────────────────────────
const AnalyticsDashboard = ({ telemetryData = [] }) => {
  const [selectedSensor, setSelectedSensor] = useState('sensorAReading');
  const [timeRange, setTimeRange] = useState('1h');
  const [historicalData, setHistoricalData] = useState({});

  const timePoints = { '15m': 15, '1h': 60, '6h': 72, '24h': 96 };

  // Rebuild historical whenever data or time range changes
  useEffect(() => {
    if (telemetryData.length === 0) return;
    const pts = timePoints[timeRange] || 60;
    const next = {};
    Object.keys(SENSOR_LABELS).forEach(key => {
      const vals = telemetryData.map(d => d[key] || 0).filter(v => v > 0);
      const avg  = vals.reduce((a, b) => a + b, 0) / (vals.length || 1);
      const { warn, crit } = SENSOR_THRESHOLDS[key];
      next[key] = buildHistorical(avg, pts, warn, crit);
    });
    setHistoricalData(next);
  }, [telemetryData, timeRange]); // eslint-disable-line

  // ── Computed stats ──────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!telemetryData.length) return {};
    const out = {};
    Object.keys(SENSOR_LABELS).forEach(key => {
      const vals = telemetryData.map(d => d[key] || 0).filter(v => v > 0);
      const avg  = vals.reduce((a, b) => a + b, 0) / (vals.length || 1);
      out[key] = { avg, min: Math.min(...vals), max: Math.max(...vals), count: vals.length };
    });
    return out;
  }, [telemetryData]);

  const healthBreakdown = useMemo(() => {
    let healthy = 0, warning = 0, critical = 0;
    telemetryData.forEach(d => {
      const isCrit = Object.keys(SENSOR_THRESHOLDS).some(k => (d[k] || 0) >= SENSOR_THRESHOLDS[k].crit);
      const isWarn = Object.keys(SENSOR_THRESHOLDS).some(k => (d[k] || 0) >= SENSOR_THRESHOLDS[k].warn);
      if (isCrit) critical++;
      else if (isWarn) warning++;
      else healthy++;
    });
    return [
      { label: 'Healthy',  value: healthy,  color: '#48bb78' },
      { label: 'Warning',  value: warning,  color: '#f6ad55' },
      { label: 'Critical', value: critical, color: '#fc8181' },
    ];
  }, [telemetryData]);

  // Per-production-line averages
  const lineStats = useMemo(() => {
    return PRODUCTION_LINES.map(line => {
      const comps = telemetryData.filter(d => d.productionLine === line);
      if (!comps.length) return { line, healthy: 0, warning: 0, critical: 0, avgTemp: 0 };
      let healthy = 0, warning = 0, critical = 0;
      comps.forEach(d => {
        const isCrit = Object.keys(SENSOR_THRESHOLDS).some(k => (d[k]||0) >= SENSOR_THRESHOLDS[k].crit);
        const isWarn = Object.keys(SENSOR_THRESHOLDS).some(k => (d[k]||0) >= SENSOR_THRESHOLDS[k].warn);
        if (isCrit) critical++; else if (isWarn) warning++; else healthy++;
      });
      const avgTemp = comps.reduce((s, d) => s + (d.sensorAReading || 0), 0) / comps.length;
      return { line, healthy, warning, critical, avgTemp: +avgTemp.toFixed(1), total: comps.length };
    });
  }, [telemetryData]);

  // Top outlier components (highest sensor readings relative to threshold)
  const topOutliers = useMemo(() => {
    return telemetryData
      .map(d => {
        let score = 0;
        const issues = [];
        Object.entries(SENSOR_THRESHOLDS).forEach(([k, t]) => {
          const v = d[k] || 0;
          if (v > 0) {
            const ratio = v / t.crit;
            score = Math.max(score, ratio);
            if (v >= t.warn) issues.push({ sensor: SENSOR_LABELS[k], value: v, unit: SENSOR_UNITS[k], level: v >= t.crit ? 'critical' : 'warning' });
          }
        });
        return { id: d.componentID, label: d.label || d.componentID, issues, score };
      })
      .filter(d => d.issues.length > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
  }, [telemetryData]);

  // OEE-style metrics (simulated from sensor health)
  const oeeMetrics = useMemo(() => {
    const total = telemetryData.length || 1;
    const critical = healthBreakdown.find(h => h.label === 'Critical')?.value || 0;
    const warning  = healthBreakdown.find(h => h.label === 'Warning')?.value  || 0;
    const availability = Math.round(100 - (critical / total) * 100 * 0.8);
    const performance  = Math.round(100 - ((warning + critical) / total) * 100 * 0.5);
    const quality      = Math.round(availability * performance / 100);
    return { availability, performance, quality };
  }, [healthBreakdown, telemetryData.length]);

  if (telemetryData.length === 0) {
    return (
      <div className="analytics-empty">
        <div className="analytics-empty-icon">📊</div>
        <h3>No Telemetry Data</h3>
        <p>Loading sensor data from factory components…</p>
      </div>
    );
  }

  const selStats = stats[selectedSensor];
  const selThresh = SENSOR_THRESHOLDS[selectedSensor];
  const selColor  = SENSOR_COLORS[selectedSensor];

  return (
    <div className="analytics-dashboard">
      {/* ── Header ── */}
      <div className="analytics-header">
        <div>
          <h2>Analytics Dashboard</h2>
          <span className="analytics-subtitle">{telemetryData.length} components · {timeRange} window</span>
        </div>
        <div className="time-range-selector">
          {['15m', '1h', '6h', '24h'].map(t => (
            <button key={t} className={`time-btn${timeRange === t ? ' active' : ''}`}
              onClick={() => setTimeRange(t)}>{t}</button>
          ))}
        </div>
      </div>

      {/* ── KPI gauges ── */}
      <div className="kpi-row">
        {Object.keys(SENSOR_LABELS).map(key => {
          const s = stats[key];
          if (!s) return null;
          const t = SENSOR_THRESHOLDS[key];
          const c = healthColor(s.avg, t);
          return (
            <div key={key}
              className={`kpi-card${selectedSensor === key ? ' selected' : ''}`}
              onClick={() => setSelectedSensor(key)}>
              <div className="kpi-title">{SENSOR_LABELS[key]}
                <span className="kpi-unit"> {SENSOR_UNITS[key]}</span>
              </div>
              <Gauge value={s.avg} max={t.max} color={c} unit={SENSOR_UNITS[key]} />
              <div className="kpi-meta">
                <span className="kpi-min">↓ {s.min.toFixed(1)}</span>
                <span className="kpi-avg" style={{ color: c }}>{s.avg.toFixed(1)}</span>
                <span className="kpi-max">↑ {s.max.toFixed(1)}</span>
              </div>
              <div className="kpi-threshold">
                <span style={{ color: '#f6ad55' }}>warn {t.warn}</span>
                <span style={{ color: '#fc8181' }}>crit {t.crit}</span>
              </div>
              <Sparkline data={(historicalData[key] || []).slice(-20)} color={SENSOR_COLORS[key]} />
            </div>
          );
        })}
      </div>

      <div className="analytics-grid">
        {/* ── Trend chart (selected sensor) ── */}
        <div className="analytics-card wide">
          <div className="card-header">
            <h4 style={{ color: selColor }}>
              {SENSOR_LABELS[selectedSensor]} ({SENSOR_UNITS[selectedSensor]}) — {timeRange} trend
            </h4>
            {selStats && (
              <div className="card-header-stats">
                <span>avg <b style={{ color: selColor }}>{selStats.avg.toFixed(1)}</b></span>
                <span>min <b>{selStats.min.toFixed(1)}</b></span>
                <span>max <b>{selStats.max.toFixed(1)}</b></span>
              </div>
            )}
          </div>
          <div className="chart-wrapper tall">
            <LineChart
              data={historicalData[selectedSensor] || []}
              color={selColor}
              height={140}
              label={selectedSensor}
              thresholdWarn={selThresh.warn}
              thresholdCrit={selThresh.crit}
            />
            <div className="chart-x-axis">
              <span>{timeRange} ago</span>
              <span>now</span>
            </div>
            <div className="chart-legend-row">
              <span style={{ color: '#f6ad55' }}>— warn {selThresh.warn}</span>
              <span style={{ color: '#fc8181' }}>— crit {selThresh.crit}</span>
            </div>
          </div>
        </div>

        {/* ── Health breakdown bar chart ── */}
        <div className="analytics-card">
          <div className="card-header"><h4>Component Health</h4></div>
          <BarChart data={healthBreakdown} height={110} />
          <div className="bar-legend">
            {healthBreakdown.map(b => (
              <div key={b.label} className="legend-item">
                <span className="legend-dot" style={{ background: b.color }} />
                <span>{b.label}</span>
                <span className="legend-value" style={{ color: b.color }}>{b.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── OEE metrics ── */}
        <div className="analytics-card">
          <div className="card-header"><h4>OEE Metrics</h4></div>
          <div className="oee-grid">
            {[
              { label: 'Availability', value: oeeMetrics.availability, color: '#48bb78' },
              { label: 'Performance',  value: oeeMetrics.performance,  color: '#4299e1' },
              { label: 'Quality',      value: oeeMetrics.quality,      color: '#9f7aea' },
            ].map(m => (
              <div key={m.label} className="oee-item">
                <OEERing value={m.value} label={m.label} color={m.color} />
                <div className="oee-label" style={{ color: m.color }}>{m.label}</div>
              </div>
            ))}
          </div>
          <div className="oee-oee">
            OEE = <span style={{ color: '#9f7aea', fontWeight: 700 }}>{oeeMetrics.quality}%</span>
          </div>
        </div>

        {/* ── Production line health ── */}
        <div className="analytics-card">
          <div className="card-header"><h4>Production Lines</h4></div>
          <div className="line-health-list">
            {lineStats.map(ls => {
              const pctOk = ls.total ? Math.round((ls.healthy / ls.total) * 100) : 0;
              const lcolor = ls.critical > 0 ? '#fc8181' : ls.warning > 0 ? '#f6ad55' : '#48bb78';
              return (
                <div key={ls.line} className="line-health-item">
                  <div className="line-health-top">
                    <span className="line-name">{ls.line}</span>
                    <span className="line-health-pct" style={{ color: lcolor }}>{pctOk}% OK</span>
                  </div>
                  <div className="line-health-bar-track">
                    <div className="line-health-bar-fill healthy"  style={{ width: `${ls.total ? (ls.healthy/ls.total)*100 : 0}%` }} />
                    <div className="line-health-bar-fill warning"  style={{ width: `${ls.total ? (ls.warning/ls.total)*100 : 0}%` }} />
                    <div className="line-health-bar-fill critical" style={{ width: `${ls.total ? (ls.critical/ls.total)*100 : 0}%` }} />
                  </div>
                  <div className="line-health-counts">
                    <span className="lhc healthy">{ls.healthy} ok</span>
                    <span className="lhc warning">{ls.warning} warn</span>
                    <span className="lhc critical">{ls.critical} crit</span>
                    <span className="lhc temp">avg {ls.avgTemp}°C</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── All sensor sparklines ── */}
        <div className="analytics-card wide">
          <div className="card-header"><h4>All Sensor Trends</h4></div>
          <div className="multi-chart-grid">
            {Object.keys(SENSOR_LABELS).map(key => {
              const s = stats[key];
              const t = SENSOR_THRESHOLDS[key];
              const c = s ? healthColor(s.avg, t) : '#718096';
              return (
                <div key={key} className={`mini-chart${selectedSensor === key ? ' active' : ''}`}
                  onClick={() => setSelectedSensor(key)} style={{ cursor: 'pointer' }}>
                  <div className="mini-chart-header">
                    <span className="mini-chart-label">{SENSOR_LABELS[key]}</span>
                    {s && <span className="mini-chart-val" style={{ color: c }}>{s.avg.toFixed(1)} {SENSOR_UNITS[key]}</span>}
                  </div>
                  <LineChart data={historicalData[key] || []} color={SENSOR_COLORS[key]}
                    height={55} label={`mini-${key}`} thresholdWarn={t.warn} />
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Outlier components table ── */}
        <div className="analytics-card wide">
          <div className="card-header"><h4>Components Needing Attention</h4></div>
          {topOutliers.length === 0 ? (
            <div className="all-clear">
              <span className="all-clear-icon">✅</span>
              <span>All components nominal</span>
            </div>
          ) : (
            <div className="outlier-table">
              {topOutliers.map(c => (
                <div key={c.id} className="outlier-row">
                  <div className="outlier-id">
                    <span className="outlier-comp-id">{c.id}</span>
                    <span className="outlier-label">{c.label}</span>
                  </div>
                  <div className="outlier-issues">
                    {c.issues.map((issue, i) => (
                      <span key={i} className={`issue-badge ${issue.level}`}>
                        {issue.sensor}: {issue.value.toFixed(1)}{issue.unit}
                      </span>
                    ))}
                  </div>
                  <div className={`outlier-status ${c.issues.some(i => i.level === 'critical') ? 'critical' : 'warning'}`}>
                    {c.issues.some(i => i.level === 'critical') ? '⚠ CRITICAL' : '⚡ WARNING'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
